/**
 * Local Storage Utilities for Token Management
 */

const TOKEN_KEYS = {
  ACCESS_TOKEN: 'client_accessToken',
  REFRESH_TOKEN: 'client_refreshToken',
  CURRENT_ROLE: 'client_currentRole',
};

/**
 * Save JWT and refresh tokens to localStorage
 */
export const saveTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
  localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
};

/**
 * Get JWT access token from localStorage
 */
export const getAccessToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
};

/**
 * Get refresh token from localStorage
 */
export const getRefreshToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
};

/**
 * Clear all tokens from localStorage
 */
export const clearTokens = (): void => {
  localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
};

/**
 * Check if access token exists
 */
export const hasAccessToken = (): boolean => {
  return !!getAccessToken();
};

/**
 * JWT payload interface
 */
interface JWTPayload {
  exp?: number;
  role?: 'tutor' | 'parent';
  userId?: string;
  [key: string]: unknown;
}

/**
 * Decode JWT token to extract payload
 * Returns null if token is invalid or cannot be decoded
 */
const decodeJWT = (token: string): JWTPayload | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

/**
 * Check if access token is expired or will expire soon
 * @param bufferMinutes - Minutes before expiration to consider token expired (default: 5)
 * @returns true if token is expired or will expire within buffer time
 */
export const isTokenExpired = (bufferMinutes: number = 5): boolean => {
  const token = getAccessToken();

  if (!token) return true;

  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return true;

  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  const bufferTime = bufferMinutes * 60 * 1000; // Convert minutes to milliseconds
  const now = Date.now();

  return now >= (expirationTime - bufferTime);
};

/**
 * Get user role from JWT access token
 * @returns 'tutor' | 'parent' | null if token is invalid or role not found
 */
export const getRoleFromToken = (): 'tutor' | 'parent' | null => {
  const token = getAccessToken();

  if (!token) return null;

  const payload = decodeJWT(token);
  if (!payload || !payload.role) return null;

  return payload.role;
};

// ============================================================
// Pending Registration (sessionStorage)
// Persists unauthenticated form data so it can be re-submitted
// automatically after the user completes login / sign-up.
// ============================================================

const PENDING_REGISTRATION_KEY = 'pendingRegistration';
const PENDING_REGISTRATION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface PendingRegistration {
  type: 'tutor' | 'parent' | 'music_tutor' | 'music_parent';
  presentation?: 'modal' | 'page';
  savedAt?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formData: Record<string, any>;
}

/**
 * Save a completed (but unauthenticated) registration form to
 * sessionStorage so it survives the LoginModal flow.
 */
export const savePendingRegistration = (
  type: PendingRegistration['type'],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formData: Record<string, any>,
  presentation: PendingRegistration['presentation'] = 'modal'
): void => {
  sessionStorage.setItem(
    PENDING_REGISTRATION_KEY,
    JSON.stringify({ type, formData, presentation, savedAt: Date.now() })
  );
};

/**
 * Retrieve the pending registration record (if any).
 */
export const getPendingRegistration = (): PendingRegistration | null => {
  const raw = sessionStorage.getItem(PENDING_REGISTRATION_KEY);
  if (!raw) return null;
  try {
    const pending = JSON.parse(raw) as PendingRegistration;
    if (pending.savedAt && Date.now() - pending.savedAt > PENDING_REGISTRATION_MAX_AGE_MS) {
      sessionStorage.removeItem(PENDING_REGISTRATION_KEY);
      return null;
    }
    return pending;
  } catch {
    sessionStorage.removeItem(PENDING_REGISTRATION_KEY);
    return null;
  }
};

/**
 * Remove the pending registration record after it has been submitted.
 */
export const clearPendingRegistration = (): void => {
  sessionStorage.removeItem(PENDING_REGISTRATION_KEY);
};
